<script>
import { Helper, debounce } from '@/mixin/helper'
import McButton from '@/components/McButton'

export default {
  components: {
    McButton,
  },
  mixins: [Helper],
  props: {
    fields: { required: true },
    apiClient: { required: true },
    limitOptions: {
      default: () => [10, 25, 50, 100],
    },
    bulkDeleteApi: {
      type: Boolean,
      default: false,
    },
    searchable: { type: Boolean },
    busy: { type: Boolean },
    tableTitle: { type: String },
    withCard: { type: Boolean },
    disabled: { type: Boolean },
    showNoData: { type: Boolean },
    trackBy: { default: 'id' },
  },
  data() {
    return {
      page: 1,
      limit: this.limitOptions[0] ?? 10,
      totalCount: 0,
      isBusy: true,
      search: '',
      sorts: [],
      items: [],
      ids: [],
      rowUpdate: {},
      isEditingRow: null,
      isAdding: false,
      isSubmitting: false,
      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
      formValues: {},
      isDisableCheckboxInPage: true,
    }
  },
  computed: {
    disabledGlobal() {
      return this.disabled || this.isEditingRow !== null || this.isBusy
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    // Fields with added defaults
    computedFields() {
      return [
        {
          key: 'checkbox',
          label: '',
        },
        ...this.fields,
        {
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
        },
      ]
    },
  },
  async mounted() {
    if (this.showNoData) {
      this.isBusy = false
      return
    }

    await this.$nextTick()
    await this.getData({
      limit: this.limit,
      page: this.page,
    })
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    transformCellValue(value) {
      if (value === false) return ''
      if (typeof value === 'number') return this.thousandFormat(value)
      return value
    },
    getInputInitialValue({ item, field }) {
      // get value from callback in field setting
      // can be used for formatting select input
      if (field.customValueFn) {
        return field.customValueFn(item) || false
      }

      // get value from item
      if (typeof item[field.key] !== 'undefined') {
        return item[field.key]
      }

      return false
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    // TECHDEBT: Only supports resourceful format
    // getData methods can be improved to support user-supplied data
    async getData(opts) {
      if (!this.apiClient?.get) {
        this.isBusy = false
        return
      }

      this.isBusy = true
      const res = await this.apiClient.get(opts)
      if (!res) return
      this.items = res.data.paginated_result
      this.ids = res.data.paginated_result
        .filter((item) => item.can_delete)
        .map((x) => x[this.trackBy])
      this.isDisableCheckboxInPage = this.ids.length <= 0
      this.page = res.metadata.page
      this.totalCount = res.metadata.total_count
      if (res.data.paginated_result.length === 0 && !this.search) {
        this.$emit('available-change', false)
      }

      this.isBusy = false
    },
    handleSearchChange: debounce(async function (_search) {
      this.selectedAll = false
      this.search = _search
      if (this.search.length > 0 && this.search.length < 3) return
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts)
      this.$refs['searchInput'].$el.focus()
    }, 500),
    handleClearSearch() {
      this.handleSearchChange(undefined)
    },
    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts)
    },
    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return
      if (this.isEditingRow !== null) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    async handleEditStart(data) {
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return
      // refetch single data from backend for data integrity
      const refetchedData = await this.apiClient.show(data.item.id)
      if (!refetchedData) return

      // update the refetched data (row) in the table
      // map is used since 'this.computedItems[index] = {}' will not work
      this.items = this.items.map((item) => {
        if (item.id == data.item.id) return refetchedData.data
        return item
      })

      this.isEditingRow = data.index
      await this.$nextTick()
      this.$refs[`${this.fields[0].key}-ref`]?.$el.querySelector('input').focus()
    },
    handleEditCancel() {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      }
      this.isEditingRow = null
    },
    // TECHDEBT: transformFields will be on formulate input instead of here
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            return { [key]: formValues[key].trim().replace(/ {2}/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    async handleSubmit(data) {
      if (this.isSubmitting) {
        this.$mcToast.warning(this.$t('global.alert.text.pleaseWait'))
        return
      }

      this.isSubmitting = true

      // Cast fields to correct types
      const transformedData = this.transformFields(data, this.fields)

      let newData = null

      if (this.isAdding) {
        newData = await this.submitStore(transformedData)
      } else {
        newData = await this.submitUpdate(transformedData, this.items[this.isEditingRow])
      }

      // If newData is false, it means there is an error
      if (newData === false) {
        this.isSubmitting = false
        return
      }

      // If newData is null, it means there is no change
      // Replace data on the table
      if (newData) {
        this.items.splice(0, 1, newData)
      }

      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
    },
    async submitStore(data) {
      const createdData = await this.apiClient.store(data)
      if (!createdData) return false

      const refetchedData = await this.apiClient.show(createdData.data.id)
      if (!refetchedData) return false

      this.ids.push(refetchedData.data.id)
      this.totalCount += 1

      this.$mcToast.success(this.$t('global.alert.text.added'))

      return await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    async submitUpdate(data, oldData) {
      const updatedData = await this.apiClient.update(oldData.id, data)
      if (!updatedData) return false

      this.$mcToast.success(this.$t('global.alert.text.changed'))

      return await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })
    },
    // eslint-disable-next-line no-unused-vars
    handleFailedValidation(errors) {
      return this.$mcToast.error(this.$t('global.inputFormatError'))
    },
    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}
      keys.forEach((key) => {
        if (key == 'id') return
        if (oldValue[key] == newValue[key]) return
        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    async handleDelete() {
      const _selected = Object.keys(this.selectedIds).filter((key) => this.selectedIds[key])
      const intSelected = []

      for (const iterator of _selected) {
        const intValue = parseInt(iterator, 10)
        if (!isNaN(intValue)) {
          intSelected.push(intValue)
        } else {
          console.log(`Unable to parse '${iterator}' as an integer.`)
        }
      }

      let res = null

      if (this.bulkDeleteApi) {
        res = await this.apiClient.delete({
          ids: intSelected,
        })
      } else {
        res = await Promise.all(intSelected.map((key) => this.apiClient.delete(key)))
      }

      if (this.search) this.handleClearSearch()
      await this.getData({
        limit: this.limit,
        page: 1,
      })
      this.handleSortChange()
      this.handleUnselectAll()
      this.$refs['mc-modal-delete'].hide()
      if (!res.status) return
      this.$mcToast.success(this.$t('global.alert.text.delete'))
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item.id]: checked }
      if (checked) {
        let flag = true
        this.selectedIdsLength += 1
        const selectedKeys = Object.keys(this.selectedIds).filter((key) => this.selectedIds[key])
        for (const iterator of this.ids) {
          let index = selectedKeys.indexOf(iterator.toString())
          if (index == -1) {
            flag = false
            break
          }
        }
        if (flag) this.selectedAll = true
        else this.selectedAll = false
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1
      }
    },
    handleSelectAll(event) {
      if (event) {
        const selectAll = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.selectedIds = Object.assign({}, this.selectedIds, selectAll)
        const total = Object.keys(this.selectedIds)
        this.selectedIdsLength = total.length
        this.selectedAll = true
      } else {
        const unselectAll = Object.fromEntries(this.ids.map((key) => [key, false]))
        this.selectedIds = Object.assign({}, this.selectedIds, unselectAll)
        this.selectedIdsLength = this.selectedIdsLength - this.ids.length
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    handleAdd() {
      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true

      this.$emit('available-change', true)

      this.items = [{ id: 0 }, ...this.items]
    },
  },
}
</script>

<template>
  <div class="mc-editable-table">
    <div class="d-flex justify-content-between align-items-center">
      <div class="fw-bold text-nowrap card-title mb-0">{{ tableTitle }}</div>
      <div class="ml-auto d-flex justify-content-end mb-3">
        <McButton
          v-if="selectedIdsLength"
          variant="secondary-red"
          class="mx-2"
          id="mc-delete-btn"
          :text="$t('global.delete')"
          icon="icon-outline_trash"
          @click="$refs['mc-modal-delete'].show()"
        />

        <McButton
          variant="primary"
          :disabled="disabledGlobal"
          id="mc-add-btn"
          text="Tambah"
          icon="icon-fill_plus"
          @click="handleAdd"
        />
      </div>
    </div>

    <div :class="{ 'card p-3 mt-2': withCard }">
      <b-row align-h="end">
        <b-col md="8" xl="10">
          <h6 class="mc-row-check mt-0" v-if="selectedIdsLength">
            <span class="badge btn-success">{{ selectedIdsLength }}</span>
            Baris terpilih.
            <a @click="handleUnselectAll" class="color-primary">Bersihkan pilihan</a>
          </h6>
        </b-col>
        <b-col md="4" xl="2" v-if="searchable" :class="{ 'mt-0': withCard, 'mt-2': !withCard }">
          <b-input-group size="md" class="mc-has-search" @click="focusSearch">
            <template #prepend>
              <i class="ps-2 mc-icon icon-fill_search"></i>
            </template>
            <b-form-input
              placeholder="Cari..."
              :disabled="disabledGlobal"
              :value="search"
              type="search"
              @input="handleSearchChange"
              ref="searchInput"
            ></b-form-input>
          </b-input-group>
        </b-col>
      </b-row>
      <FormulateForm
        v-model="formValues"
        name="editable-table-form"
        ref="formRef"
        @submit="handleSubmit"
        @failed-validation="handleFailedValidation"
      >
        <div class="table-responsive my-3 border border-1 mc-table-overflow">
          <b-table
            :busy="isBusy"
            :items="items"
            :fields="computedFields"
            :per-page="limit"
            fixed
            show-empty
            class="editable-table"
          >
            <!-- Loading -->
            <template #table-busy>
              <div class="__busy">
                <b-spinner class="align-middle mb-2"></b-spinner>
                <strong>Loading...</strong>
              </div>
            </template>

            <!-- Empty Result -->
            <template #empty>
              <div class="text-center my-4">Tidak ada data</div>
            </template>

            <!-- Slot: Header Checkbox -->
            <template #head(checkbox)>
              <div class="form-check" style="width: 10px">
                <b-form-checkbox
                  @change="handleSelectAll"
                  v-model="selectedAll"
                  :disabled="disabledGlobal || isDisableCheckboxInPage"
                ></b-form-checkbox>
              </div>
            </template>

            <!-- Slot: Header Data -->
            <template #head()="data">
              <div @click="handleSortChange(data)">
                {{ data.label }}
                <template v-if="data.field.isSortable">
                  <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                    <i class="mc-icon icon-outline_chevron-up"></i>
                  </div>
                  <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                    <i class="mc-icon icon-outline_chevron-down"></i>
                  </div>
                </template>
              </div>
            </template>

            <!-- Slot: Cell Checkbox -->
            <template #cell(checkbox)="data">
              <div class="form-check">
                <b-form-checkbox
                  @change="(event) => handleSelect(event, data)"
                  :checked="selectedIds[data.item.id]"
                  :disabled="disabledGlobal || !data.item.can_delete"
                ></b-form-checkbox>
              </div>
            </template>

            <!-- Slot: All Cells -->
            <template #cell()="{ index, field, value, item }">
              <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
                <FormulateInput
                  v-bind="{ ...field }"
                  :rowItem="item"
                  :label="null"
                  :name="field.inputName || field.key"
                  :type="field.type || 'text'"
                  :value="getInputInitialValue({ field, item })"
                  :disabled="isSubmitting"
                  validation-behavior="live"
                  error-behavior="live"
                  :ref="`${field.key}-ref`"
                />
              </template>

              <!-- Bold First Column -->
              <template v-else-if="field.isRedirectable">
                <div class="fw-bold text-primary">
                  {{ value }}
                </div>
              </template>

              <template v-else>
                {{ transformCellValue(value) }}
              </template>
            </template>

            <!-- Slot: Action Column (Last Column) -->
            <template #cell(action)="data">
              <div v-if="isEditingRow == data.index">
                <b-button variant="outline" class="__accept-button btn" type="submit">
                  <i class="mc-icon icon-fill_checkmark"></i>
                </b-button>

                <b-button
                  variant="outline"
                  class="__cancel-button btn"
                  @click="handleEditCancel(data)"
                >
                  <i class="mc-icon icon-outline_close"></i>
                </b-button>
              </div>

              <b-button
                v-else
                variant="outline"
                :class="{
                  '__edit-button btn': true,
                  __disabled: disabled || isEditingRow !== null || isBusy || !data.item.can_edit,
                }"
                @click="handleEditStart(data)"
              >
                <i v-if="data.item.can_edit" class="mc-icon icon-outline_edit"></i>
              </b-button>
            </template>
          </b-table>
        </div>
      </FormulateForm>
      <div class="d-flex flex-wrap flex-md-nowrap">
        <div class="mc-limit">
          <div class="__limit">
            <div>Menampilkan</div>
            <b-form-select
              class="form-select form-select-sm __limit--dropdown"
              size="sm"
              :disabled="disabledGlobal"
              :options="limitOptions"
              :value="limit"
              @input="handleLimitChange"
            >
            </b-form-select>
            <div>data.</div>
          </div>

          <div class="__info" v-if="totalCount === 0">
            <strong>{{ totalCount }}</strong> dari <strong>{{ totalCount }}</strong> data.
          </div>

          <div class="__info" v-else>
            <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong> dari
            <strong>{{ totalCount }}</strong> data.
          </div>
        </div>
        <div class="mc-pagination-container">
          <b-pagination
            :disabled="disabledGlobal"
            :per-page="limit"
            :total-rows="totalCount"
            :value="page"
            @change="handlePageChange"
          ></b-pagination>
        </div>
      </div>
    </div>
    <b-modal
      class="mc-modal"
      id="mc-modal-delete"
      ref="mc-modal-delete"
      title="Hapus"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>
          Apakah Anda yakin ingin menghapus <strong>{{ selectedIdsLength }}</strong> data berikut ?
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.cancel')"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.delete')" @click="handleDelete" />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
@import './style.scss';
</style>
